"use client";

import React, { useState } from "react";
import { Building2, Landmark, ShieldCheck, ArrowRight, Activity, ChartBar, Lock } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const oversightAreas = [{
  title: "Digital Wallet Recovery",
  shortDesc: "Regain access to lost private keys and assets.",
  deepSummary: "Our specialists utilize advanced cryptographic techniques and proprietary software to recover access to hardware wallets, lost seed phrases, and legacy accounts with clinical precision.",
  icon: Lock,
  secondaryIcon: ShieldCheck,
  link: "/ForensicServicesPage",
  modelType: "wallet"
}, {
  title: "Scam & Fraud Investigation",
  shortDesc: "Deep-dive analysis of malicious digital actors.",
  deepSummary: "Comprehensive intelligence gathering on phishing schemes, rug pulls, and investment fraud. We provide detailed evidentiary reports to support legal action and asset seizure efforts.",
  icon: Activity,
  secondaryIcon: Building2,
  link: "/ForensicServicesPage",
  modelType: "scam"
}, {
  title: "Blockchain Forensics",
  shortDesc: "Precision tracing of complex illicit transactions.",
  deepSummary: "Utilizing institutional-grade forensic tools to map transaction paths across multiple chains. We identify exit points, mixing services, and cold storage destinations with high-fidelity accuracy.",
  icon: ChartBar,
  secondaryIcon: Landmark,
  link: "/ForensicServicesPage",
  modelType: "forensics"
}];
export default function StrategicOversightAreas() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  return <section data-section-id="50971" id="our-recovery-specializations" className="relative py-24 dark bg-background text-foreground overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 z-0">
          <div className="w-full h-full bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.1]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-primary/10 blur-[120px] rounded-full opacity-30" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 font-sans uppercase">
            Our Recovery Specializations
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-sans">
            Expert-led forensic solutions designed to reclaim your digital assets and secure your financial future through institutional-grade blockchain analysis.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10", "transition-all duration-1000 ease-out")}>
          {oversightAreas.map((area, i) => {
          const IconComponent = area.icon;
          const SecondaryIcon = area.secondaryIcon;
          return <div key={i} data-index={i} className="h-full" onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
                <HoverCard openDelay={0} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <div className="h-full cursor-pointer group">
                      <Card className="h-full bg-card/40 backdrop-blur-xl border-primary/20 hover:border-primary/60 transition-all duration-500 hover:shadow-[0_0_30px_-5px_hsla(180,100%,50%,0.3)] hover:-translate-y-1 overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        
                        <CardHeader className="relative z-10 pb-2">
                          <div className="mb-6 inline-flex items-center justify-center size-16 rounded-sm bg-primary/5 text-primary border border-primary/30 group-hover:scale-110 group-hover:bg-primary/10 transition-all duration-500 shadow-[0_0_15px_rgba(0,255,255,0.1)]">
                            <IconComponent className="size-8" />
                          </div>
                          <CardTitle className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors duration-300 font-sans">
                            {area.title}
                          </CardTitle>
                        </CardHeader>
                        
                        <CardContent className="relative z-10">
                          <CardDescription className="text-base text-muted-foreground mb-6 group-hover:text-foreground/90 transition-colors font-sans">
                            {area.shortDesc}
                          </CardDescription>
                          
                          <div className="flex items-center text-sm font-medium text-primary opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 font-mono">
                            VIEW CASE STUDY <ArrowRight className="ml-2 size-4" />
                          </div>
                        </CardContent>

                        <div className="absolute top-0 right-0 size-32 -mr-8 -mt-8 opacity-10 group-hover:opacity-30 transition-opacity pointer-events-none flex items-center justify-center">
                          <SecondaryIcon className="size-24" />
                        </div>
                      </Card>
                    </div>
                  </HoverCardTrigger>
                  
                  <HoverCardContent side="top" align="center" className="w-80 md:w-96 bg-card border-primary/30 shadow-2xl shadow-black/80 p-6 backdrop-blur-2xl">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-primary">
                        <IconComponent className="size-5" />
                        <h4 className="font-semibold text-foreground uppercase tracking-wider">{area.title}</h4>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                        {area.deepSummary}
                      </p>
                      <div className="pt-2">
                        <Button asChild variant="outline" size="sm" className="w-full border-primary/30 hover:bg-primary/20 hover:text-primary hover:border-primary transition-all duration-300 font-mono text-xs">
                          <Link to={area.link}>
                            INITIATE INVESTIGATION
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>;
        })}
        </div>
      </div>
    </section>;
}